import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import PageLayout from '../../components/PageLayout';
import DataTable from '../../components/ui/DataTable';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import { CustomerCreateModal } from '../../components/CustomerModals';
import { CustomerImportModal } from '../../components/CustomerImportModal';
import toast from 'react-hot-toast';
import { History, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useOperationsAPI } from '../../context/useOperationsAPI';
import { getOriginalHostHeaderValue } from '../../services/api';
import { customerImportScope } from '../../types/customer-import';
import {
  UserIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PlusIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

interface ResyncSummary {
  connectedAccountId: string;
  localCustomersProcessed: number;
  skippedImportCustomers?: number;
  stripeCustomersProcessed: number;
  linkedLocalByStripeId: number;
  linkedLocalByEmail: number;
  createdStripeFromLocal: number;
  createdLocalFromStripe: number;
}

function AdminCustomers() {
  const { user, operationsCompanyId } = useAuth();
  const scope = customerImportScope(getOriginalHostHeaderValue(), operationsCompanyId || user?.company_id, user?.id);
  if (!scope) return <div role="alert">Select an authorized company to view customers.</div>;
  return <ScopedAdminCustomers key={scope} scope={scope} />;
}

function ScopedAdminCustomers({ scope }: { scope: string }) {
  const scopedAPI = useOperationsAPI(adminAPI);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    kycStatus: '',
    loyaltyTier: '',
    page: 1,
    limit: 20
  });
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const isImportModalOpen = new URLSearchParams(location.search).get('import') === 'customers';
  const initialBatchId = new URLSearchParams(location.search).get('importBatch') || undefined;
  const [historyOffset, setHistoryOffset] = useState(0);
  const history = useQuery(['customer-import-history', scope, historyOffset],
    () => scopedAPI.listCustomerImportBatches({ offset: historyOffset, limit: 20 }),
    { select: (response) => response.data, staleTime: 0, keepPreviousData: false });
  const [lastResyncSummary, setLastResyncSummary] = useState<ResyncSummary | null>(null);
  const [lastResyncAt, setLastResyncAt] = useState<Date | null>(null);

  const setPageIntent = useCallback((intent?: 'create' | 'import', batchId?: string) => {
    const params = new URLSearchParams(location.search);
    params.delete('create');
    params.delete('import');
    params.delete('importBatch');

    if (intent === 'create') {
      params.set('create', 'customer');
    }
    if (intent === 'import') {
      params.set('import', 'customers');
      if (batchId) params.set('importBatch', batchId);
    }

    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const resyncCustomersMutation = useMutation(
    () => scopedAPI.resyncCustomers(),
    {
      onSuccess: (response) => {
        if (!live.current) return;
        const summary = response.data as ResyncSummary;
        setLastResyncSummary(summary);
        setLastResyncAt(new Date());
        toast.success(
          `Resync complete: ${summary.createdStripeFromLocal} created in Stripe, ${summary.createdLocalFromStripe} added locally, ${summary.linkedLocalByEmail + summary.linkedLocalByStripeId} linked. ${summary.skippedImportCustomers || 0} rolled-back import records left unchanged.`
        );
        queryClient.invalidateQueries(['admin-customers', scope]);
      },
      onError: (error: any) => {
        if (!live.current) return;
        toast.error(error?.response?.data?.message || 'Failed to resync customers');
      },
    }
  );

  const handleResyncCustomers = () => {
    if (resyncCustomersMutation.isLoading) {
      return;
    }

    const confirmed = window.confirm(
      'Run customer resync now? This will link existing records and create any missing local or Stripe customers for this tenant.'
    );
    if (!confirmed) {
      return;
    }

    resyncCustomersMutation.mutate();
  };

  useEffect(() => {
    const state = location.state as { openCustomer?: any } | null;
    const customerFromPalette = state?.openCustomer;

    if (!customerFromPalette) {
      return;
    }

    setSelectedCustomer(customerFromPalette);

    // Clear one-time navigation state so refresh/navigation does not re-open unexpectedly.
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setIsCreateModalOpen(params.get('create') === 'customer');
  }, [location.search]);

  const { data: customersData, isLoading } = useQuery(
    ['admin-customers', scope, filters],
    () => scopedAPI.getCustomers(filters),
    { 
      select: (response) => response.data,
      keepPreviousData: true
    }
  );

  const customers = customersData?.customers || [];
  const pagination = customersData?.pagination;
  const summary = customersData?.summary;
  const customerTableRows = customers.map((customer: any) => ({
    ...customer,
    display_name: [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email,
  }));

  const tableColumns = [
    {
      key: 'display_name',
      header: 'Customer',
      render: (customer: any) => (
        <div className="min-w-0">
          <p className="font-medium text-on-surface truncate">{customer.display_name}</p>
          <p className="text-xs text-on-surface-variant truncate">{customer.email || 'No email'}</p>
        </div>
      ),
    },
    {
      key: 'id',
      header: 'Stripe ID',
      render: (customer: any) => (
        <div>
          <span className="font-mono text-xs text-on-surface-variant">{customer.id}</span>
          {!!customer.historical_stripe_customer_ids?.length && (
            <p className="mt-1 text-xs text-on-surface-variant">{customer.historical_stripe_customer_ids.length} historical billing profile{customer.historical_stripe_customer_ids.length === 1 ? '' : 's'}</p>
          )}
          {customer.identity_reconciliation_required && <p className="mt-1 text-xs text-error">Identity review required</p>}
        </div>
      ),
    },
    {
      key: 'customer_status',
      header: 'Status',
      render: (customer: any) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-pill ${
          customer.customer_status === 'Active'
            ? 'bg-success-container text-success-on-container'
            : 'bg-error-container text-error-on-container'
        }`}>
          {customer.customer_status || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'registrationDate',
      header: 'Created',
      render: (customer: any) => (
        <span>{new Date(customer.registrationDate).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'lastActivity',
      header: 'Last Synced',
      render: (customer: any) => (
        <span>{new Date(customer.lastActivity).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (customer: any) => (
        <button
          type="button"
          className="btn-secondary px-3 py-1.5 text-xs"
          onClick={() => setSelectedCustomer(customer)}
        >
          Open
        </button>
      ),
    },
  ];

  return (
    <PageLayout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-on-surface">Stripe Customers</h1>
            <p className="mt-2 text-on-surface-variant">Customers synced directly from your connected Stripe account</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleResyncCustomers}
              disabled={resyncCustomersMutation.isLoading}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowPathIcon className={`h-5 w-5 ${resyncCustomersMutation.isLoading ? 'animate-spin' : ''}`} />
              {resyncCustomersMutation.isLoading ? 'Resyncing...' : 'Resync Local + Stripe'}
            </button>
            <button
              type="button"
              onClick={() => setPageIntent('import')}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setPageIntent('create')}
              className="btn-primary inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Customer
            </button>
          </div>
        </div>
      </div>

      <section className="card p-4 mb-6 space-y-3" aria-label="Customer import history">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold flex items-center gap-2"><History className="h-4 w-4" /> Customer import history</h2>
          <button type="button" className="btn-secondary" onClick={() => { void history.refetch(); }} disabled={history.isFetching}>Refresh history</button>
        </div>
        {history.isLoading || history.isFetching ? <p role="status">Loading import history...</p>
          : history.isError ? <p role="alert">Import history unavailable. Refresh history to retry.</p>
          : history.data?.items.length ? <div className="overflow-auto">
            <table className="min-w-full text-sm divide-y divide-surface-dim">
              <thead><tr>
                <th className="text-left p-2">File / created</th><th className="text-left p-2">Status</th>
                <th className="text-left p-2">Outcomes</th><th className="p-2">Review</th>
              </tr></thead>
              <tbody>{history.data.items.map((batch) => <tr key={batch.id}>
                <td className="p-2">{batch.sourceFilename || 'Customer CSV'}<div className="text-xs text-on-surface-variant">{new Date(batch.createdAt).toLocaleString()}</div></td>
                <td className="p-2">{batch.status.replace(/_/g, ' ')}</td>
                <td className="p-2">{batch.totalRows} total; {batch.createdCount} activated; {batch.skippedCount} skipped; {batch.reconciliationRequiredCount} require reconciliation; {batch.failedCount} failed</td>
                <td className="p-2"><button type="button" className="btn-secondary" onClick={() => setPageIntent('import', batch.id)}>Open import</button></td>
              </tr>)}</tbody>
            </table>
          </div> : <p>No customer imports yet.</p>}
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm">{history.data ? `${history.data.total ? historyOffset + 1 : 0}–${Math.min(historyOffset + history.data.items.length, history.data.total)} of ${history.data.total}` : 'Import history'}</span>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary" aria-label="Previous import page" disabled={historyOffset === 0 || history.isFetching} onClick={() => setHistoryOffset((offset) => Math.max(0, offset - 20))}><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" className="btn-secondary" aria-label="Next import page" disabled={!history.data || history.isError || history.isFetching || historyOffset + 20 >= history.data.total} onClick={() => setHistoryOffset((offset) => offset + 20)}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </section>

      {lastResyncSummary && lastResyncAt && (
        <div className="card p-4 mb-6 border border-blue-100 bg-blue-50 text-blue-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Last Customer Resync</h3>
              <p className="text-xs mt-1">
                {lastResyncAt.toLocaleString()} on account {lastResyncSummary.connectedAccountId}
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide">Linked</p>
              <p className="font-semibold">{lastResyncSummary.linkedLocalByStripeId + lastResyncSummary.linkedLocalByEmail}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Created in Stripe</p>
              <p className="font-semibold">{lastResyncSummary.createdStripeFromLocal}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Created Locally</p>
              <p className="font-semibold">{lastResyncSummary.createdLocalFromStripe}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Processed</p>
              <p className="font-semibold">{lastResyncSummary.localCustomersProcessed} local / {lastResyncSummary.stripeCustomersProcessed} Stripe</p>
            </div>
          </div>
          {!!lastResyncSummary.skippedImportCustomers && (
            <p role="status" className="mt-3 text-sm">
              {lastResyncSummary.skippedImportCustomers} rolled-back import records were left unchanged and require identity reconciliation before editing or syncing. Excluding import rows does not restore those customers.
            </p>
          )}
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="card p-4">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Total Customers</p>
                <p className="text-2xl font-bold text-on-surface">{summary.totalCustomers}</p>
              </div>
            </div>
          </div>
          
          {/* <div className="card p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Verified</p>
                <p className="text-2xl font-bold text-on-surface">{summary.verifiedCustomers}</p>
              </div>
            </div>
          </div> */}
          
          {/* <div className="card p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Avg LTV</p>
                <p className="text-2xl font-bold text-on-surface">${summary.averageLifetimeValue}</p>
              </div>
            </div>
          </div>
           */}
          {/* <div className="card p-4">
            <div className="flex items-center">
              <XMarkIcon className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Churn Rate</p>
                <p className="text-2xl font-bold text-on-surface">{summary.churnRate}%</p>
              </div>
            </div>
          </div> */}
        </div>
      )}

      {!summary && (
        <div className="card p-4 mb-6 flex items-start gap-3 bg-blue-50 border border-blue-100 text-blue-900">
          <ArrowPathIcon className="h-5 w-5 mt-0.5" />
          <p className="text-sm">
            This view now reads customers directly from Stripe. Legacy user-based customer records are not used for this page.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                className="input-field pl-10"
                placeholder="Search customers..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-on-surface-variant absolute left-3 top-2.5" />
            </div>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">KYC Status</label>
            <select
              className="input-field"
              value={filters.kycStatus}
              onChange={(e) => setFilters({ ...filters, kycStatus: e.target.value, page: 1 })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
            </select>
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Loyalty Tier</label>
            <select
              className="input-field"
              value={filters.loyaltyTier}
              onChange={(e) => setFilters({ ...filters, loyaltyTier: e.target.value, page: 1 })}
            >
              <option value="">All Tiers</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div> */}

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', kycStatus: '', loyaltyTier: '', page: 1, limit: 20 })}
              className="btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <DataTable
          columns={tableColumns}
          data={customerTableRows}
          loading={isLoading}
          onRowClick={(customer) => {
            setSelectedCustomer(customer);
          }}
          emptyState={
            <div className="text-center py-6">
              <UserIcon className="h-10 w-10 text-on-surface-variant mx-auto mb-3" />
              <h3 className="text-base font-medium text-on-surface mb-1">No customers found</h3>
              <p className="text-on-surface-variant">No customers match your current filters.</p>
              <button
                type="button"
                onClick={() => setPageIntent('create')}
                className="btn-primary mt-4 inline-flex items-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Create Customer
              </button>
            </div>
          }
        />

        {pagination && pagination.totalPages > 1 && (
          <div className="bg-surface-container-low px-6 py-3 flex items-center justify-between rounded-card">
            <div className="text-sm text-on-surface-variant">
              Page {filters.page} of {pagination.totalPages} ({pagination.total} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page <= 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.totalPages}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <CustomerDetailDrawer
        open={Boolean(selectedCustomer)}
        onClose={() => setSelectedCustomer(null)}
        customer={selectedCustomer}
        allowEdit
      />

      <CustomerCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setPageIntent()}
      />

      <CustomerImportModal
        isOpen={isImportModalOpen}
        initialBatchId={initialBatchId}
        onNewImport={() => setPageIntent('import')}
        onClose={() => setPageIntent()}
      />

    </PageLayout>
  );
}

export default AdminCustomers;
